import React, { useId } from 'react'
import { tv } from 'tailwind-variants'
import Badge from 'components/badge'

// In the manner of shadcn/ui's choice cards: each option a bordered card, its label (and a badge, if it has
// one) over a line saying more, with the radio at the right. The chosen card stays white, for legibility, and
// takes a blue border, as its radio takes a blue dot.
const radio = tv({
  slots: {
    option: [
      'flex w-full cursor-pointer items-start gap-3 rounded-lg border px-3.5 py-3 text-left outline-none',
      'transition-colors focus-visible:ring-3 focus-visible:ring-blue/15'
    ],
    circle: 'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border bg-surface transition-colors',
    dot: 'size-2 rounded-full bg-blue',
    label: 'text-[13px] font-medium leading-5 text-body',
    description: 'text-pretty text-xs leading-4 text-muted'
  },
  variants: {
    checked: {
      true: { option: 'border-blue bg-surface', circle: 'border-blue' },
      false: { option: 'border-divider hover:bg-surface-hover/60', circle: 'border-faint' }
    },
    disabled: {
      true: { option: 'cursor-default opacity-50 hover:bg-transparent' }
    }
  }
})

export interface RadioOption<T extends string> {
  value: T
  label: string
  description?: string
  /** A short note beside the label, as a small pill: "Paid seat required" in grey, "Current" in blue. */
  badge?: { label: string; tone?: 'muted' | 'blue' }
  disabled?: boolean
}

interface RadioGroupProps<T extends string> {
  options: RadioOption<T>[]
  value: T
  onChange: (value: T) => void
  /** The id of the label naming the group, or its name itself with `label`. */
  labelledBy?: string
  label?: string
}

/**
 * Choosing one of a few options that each need a word of explanation. As in a radio group, Tab reaches the
 * chosen option and the arrow keys move between them, choosing as they go; Space chooses the one focused.
 * Each option is named by its label and described by its badge and the line under it.
 */
const RadioGroup = <T extends string>({
  options,
  value,
  onChange,
  labelledBy,
  label
}: RadioGroupProps<T>): React.ReactElement => {
  const id = useId()
  const enabled = options.filter((option) => !option.disabled)

  const move = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault()
      event.currentTarget.click()
      return
    }
    const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[event.key]
    if (step === undefined || enabled.length === 0) return
    event.preventDefault()
    const index = enabled.findIndex((option) => option.value === value)
    const next = enabled[(index + step + enabled.length) % enabled.length]
    onChange(next.value)
    event.currentTarget.parentElement?.querySelector<HTMLElement>(`[data-value="${next.value}"]`)?.focus()
  }

  return (
    <div role="radiogroup" aria-labelledby={labelledBy} aria-label={label} className="flex flex-col gap-2">
      {options.map((option) => {
        const checked = option.value === value
        const styles = radio({ checked, disabled: !!option.disabled })
        const optionId = `${id}-${option.value}`
        const described = [option.badge && `${optionId}-badge`, option.description && `${optionId}-description`]
          .filter(Boolean)
          .join(' ')
        return (
          <div
            key={option.value}
            role="radio"
            aria-checked={checked}
            aria-disabled={option.disabled || undefined}
            aria-labelledby={`${optionId}-label`}
            aria-describedby={described || undefined}
            data-value={option.value}
            // Only the chosen option is a tab stop, as in a native radio group.
            tabIndex={checked ? 0 : -1}
            className={styles.option()}
            onClick={() => {
              if (!option.disabled) onChange(option.value)
            }}
            onKeyDown={move}
          >
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="flex items-center gap-2">
                <span id={`${optionId}-label`} className={styles.label()}>
                  {option.label}
                </span>
                {option.badge && (
                  <Badge id={`${optionId}-badge`} tone={option.badge.tone}>
                    {option.badge.label}
                  </Badge>
                )}
              </span>
              {option.description && (
                <span id={`${optionId}-description`} className={styles.description()}>
                  {option.description}
                </span>
              )}
            </span>
            <span className={styles.circle()} aria-hidden>
              {checked && <span className={styles.dot()} />}
            </span>
          </div>
        )
      })}
    </div>
  )
}

export default RadioGroup
